import React, { useState, useEffect, useRef, useCallback } from "react";
import images from "../../../images";
import { FormField } from "../../../components/common/Input/Input";
import { FormSelect } from "../../../components/common/Select/FormSelect";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import CommonModal from "../../../components/common/Modal/ModalCommon";
import Button from "../../../components/common/Button/Button";
import {
  useGetUserInfoQuery,
  useUpdateUserInfoMutation,
} from "../../../services/apiSlice";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LoadingCommon from "../../../components/common/LoadingStatus/LoadingCommon";
import NewHeader from "../../Home/NewHeader";
import Breadcrumb from "../ProfileCommon/Breadcrumb";
import PhoneInputProps from "../../../components/PhoneInputProps/PhoneInputProps";
// import { formatPhoneNumber } from "../../../Utils/helper";

const EditUser = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const updateModalRef = useRef();

  const [formData, setFormData] = useState({
    username: "",
    phone: "",
    age: "",
    gender: "",
    country_code: "",
    phoneNumber: "",
  });
  const locationOptions = [{ label: "Singapore (SGD)", value: "SGD" }];

  const defaultLocationOption = locationOptions[0];
  const {
    data: userInfo,
    isLoading: isLoadingProfile,
    refetch,
  } = useGetUserInfoQuery();
  const [updateUserInfo] = useUpdateUserInfoMutation();

  useEffect(() => {
    if (userInfo?.data) {
      const { username, country_code, phone, age, gender } = userInfo.data;

      const rawPhone =
        typeof phone === "string" ? phone.replace(/[^0-9]/g, "") : "";

      const phoneFirst8Digits = rawPhone.slice(0, 8);

      const formattedPhoneNumber =
        phoneFirst8Digits.length > 4
          ? `${phoneFirst8Digits.slice(0, 4)}-${phoneFirst8Digits.slice(4, 8)}`
          : phoneFirst8Digits;

      const formattedPhone =
        country_code && phone ? `${country_code}${phone}` : "";

      setFormData((prev) => ({
        ...prev,
        username: username || "",
        phone: formattedPhone,
        age: age || "",
        gender: gender || "male",
        phoneNumber: formattedPhoneNumber || "",
      }));
    }
  }, [userInfo]);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  }, []);

  // const handlePhoneChange = useCallback((value) => {
  //   setFormData((prev) => ({ ...prev, phone: value || "" }));
  // }, []);

  const handleGenderChange = useCallback((selectedOption) => {
    if (selectedOption) {
      if (selectedOption.value) {
        setFormData((prev) => ({
          ...prev,
          gender: selectedOption.value,
        }));
      } else if (typeof selectedOption === "string") {
        setFormData((prev) => ({
          ...prev,
          gender: selectedOption,
        }));
      } else if (selectedOption.target && selectedOption.target.value) {
        setFormData((prev) => ({
          ...prev,
          gender: selectedOption.target.value,
        }));
      }
    } else {
    }
  }, []);

  const parsePhoneNumber = (phone) => {
    if (!phone) return { countryCode: "", phoneNumber: "" };

    const parsedPhone = parsePhoneNumberFromString(phone);
    return parsedPhone
      ? {
          countryCode: `+${parsedPhone.countryCallingCode}`,
          phoneNumber: parsedPhone.nationalNumber,
        }
      : { countryCode: "", phoneNumber: "" };
  };

  const handleConfirmUpdate = useCallback(async () => {
    const { countryCode, phoneNumber } = parsePhoneNumber(formData.phone);

    const body = {
      username: formData.username,
      age: formData.age,
      country_code: countryCode,
      phone: phoneNumber,
      gender: formData.gender,
    };

    try {
      const response = await updateUserInfo(body).unwrap();
      if (
        response.message === "Operation successful" ||
        response.status === 200
      ) {
        toast.success(t("toast.success.updateSuccess"));
        await refetch();
        updateModalRef?.current?.closeModal();
        navigate("/profile");
      }
    } catch (error) {
      toast.error(t("toast.error.updateFailed"));
    }
  }, [formData, updateUserInfo, navigate, t, refetch]);

  const handleUpdate = useCallback(() => {
    updateModalRef?.current?.openModal();
  }, []);

  const genderOptions = [
    { value: "male", label: t("signUp.gender.male") },
    { value: "female", label: t("signUp.gender.female") },
    { value: "maleIdentifying", label: t("signUp.gender.maleIdentifying") },
    {
      value: "femaleIdentifying",
      label: t("signUp.gender.femaleIdentifying"),
    },
    { value: "nonBinary", label: t("signUp.gender.nonBinary") },
    { value: "agender", label: t("signUp.gender.agender") },
    { value: "preferNotToSay", label: t("signUp.gender.preferNotToSay") },
  ];

  return (
    <div className="flex overflow-hidden flex-col mx-auto w-full bg-white max-w-[480px]">
      <LoadingCommon statusLoading={isLoadingProfile} />
      <div className="flex flex-col w-full min-h-[912px]">
        <div className="fixed top-0 left-0 w-full bg-white z-50">
          <NewHeader />
        </div>
        <div className="h-[30px]"></div>
        <div className="flex flex-col flex-1 justify-start gap-3 py-3.5 w-full px-[30px]">
          <Breadcrumb
            breadCrumbTiltle={t("editUser.title")}
            onClickBack={() => navigate("/profile")}
          />
          <form className="flex flex-col gap-6 w-full text-sm leading-none text-black">
            <FormField
              icon={images.StartIcon}
              placeholder={t("editUser.username")}
              name="username"
              value={formData.username}
              onChange={handleChange}
              classNameInput="text-[#333333]"
            />
            <PhoneInputProps
              value={formData.phoneNumber}
              readOnly={true}
              className=" min-h-[50px]"
              // onChange={handlePhoneChange}
            />
            <FormField
              icon={images.CalendarIcon2}
              placeholder={t("editUser.age")}
              name="age"
              type="number"
              value={formData.age}
              maxLength={3}
              onChange={handleChange}
              classNameInput="text-[#333333]"
            />

            <FormSelect
              icon={images.User3Icon}
              value={genderOptions.find(
                (option) =>
                  option.value.toLowerCase() === formData.gender.toLowerCase()
              )}
              onChange={handleGenderChange}
              options={genderOptions}
              name="gender"
              classNameText="text-[#333333]"
            />

            <FormSelect
              icon={images.User3Icon}
              value={defaultLocationOption}
              onChange={() => {}}
              options={locationOptions}
              name="subject"
              isDisabled={true}
              classNameText="text-[#333333]"
            />
          </form>

          <div className="flex flex-col gap-3.5  w-full mb-6 items-center">
            <button
              onClick={handleUpdate}
              className="flex-1 shrink gap-2.5 self-stretch px-5 mt-4  text-xl font-medium text-white whitespace-nowrap bg-[#253FF6] rounded-xl min-h-[50px]"
            >
              {t("editUser.saveProfile")}
            </button>

            {/* <span className="text-[#A0A0A0] border-b border-[#A0A0A0] cursor-pointer mt-4">
              {t("editUser.deleteAccount")}
            </span> */}
          </div>
        </div>
      </div>

      <CommonModal
        ref={updateModalRef}
        title="Are you sure you want to update your profile information?"
        actions={(closeModal) => (
          <>
            <Button
              text={t("base.cancel")}
              className={
                "flex-1 shrink gap-2.5 self-stretch px-5 py-3 rounded-xl min-h-[40px] bg-white border border-solid border-stone-200 text-[#A0A0A0]"
              }
              onClick={() => closeModal(false)}
            />
            <Button
              text={t("base.update")}
              className={
                "flex-1 shrink gap-2.5 self-stretch px-5 py-3 rounded-xl min-h-[40px] text-white bg-[#253FF6]"
              }
              variant="primary"
              onClick={handleConfirmUpdate}
            />
          </>
        )}
      >
        <div className="flex flex-col justify-center items-center">
          <img src={images.UpdateModalImage} alt="" className="w-[80px]" />
        </div>
      </CommonModal>
    </div>
  );
};

export default EditUser;
